import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Icon, IconButton, SortButton, ESortOptions as ESortOptions$1 } from "@flatfile/design-system";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import ReadOnly from "../../../common/assets/icons/ReadOnly.js";
import { ESortOptions } from "../../Table/types.js";
import { useColumns } from "../../../context/useColumnsContext.js";
import { MAX_PINNED_COLUMNS } from "../../../hooks/usePinColumns.js";
import { useResizeColumn } from "../../../hooks/useResizeColumn.js";
import { EColumnType } from "../../../types/Column.js";
import { determineSort } from "../../../utils/determineSort.js";
import { columnHeaderClickableTooltipId, columnHeaderTooltipId } from "../../../utils/utils.js";
import { useTranslation } from "react-i18next";
import { useFindAndReplaceContext } from "../../../context/useFindAndReplaceContext.js";
import { useSearchValueContext } from "../../../context/useSearchValueContext.js";
import { htmlStringContainsLinks } from "../../../utils/htmlStringContainsLinks.js";
import { DragHandle } from "./DragHandleStyles.js";
import { IndexRowCellInner, ColumnHeaderIcon, StyledLabel, IndexRowCellRight, IndexButtonsContainer } from "./Styles.js";
const convertSortOrder = (order) => {
  switch (order) {
    case "asc":
      return ESortOptions.ascending;
    case "desc":
      return ESortOptions.descending;
    default:
      return ESortOptions.noOrder;
  }
};
const convertButtonSortOrder = (order) => {
  switch (order) {
    case ESortOptions.ascending:
      return ESortOptions$1.Asc;
    case ESortOptions.descending:
      return ESortOptions$1.Desc;
    default:
      return ESortOptions$1.None;
  }
};
const columnChangeIcon = (changeType) => {
  switch (changeType) {
    case "added":
      return "diff";
    case "removed":
      return "trash";
    case "changed":
      return "diff";
    default:
      return void 0;
  }
};
const ColumnHeaderContent = ({
  column,
  columnChangeType,
  pinned,
  onSort,
  onSearchValue,
  onFindAndReplace,
  onClick,
  sortOverride,
  absoluteColumnIndex,
  cellRef,
  tableId,
  readOnly,
  useFilterByValue,
  pinningEnabled,
  showColumnMenu,
  columnContextMenuSlot,
  isLastColumn,
  headerInjections
}) => {
  var _a;
  const dragHandleRef = useRef(null);
  const { t } = useTranslation();
  const value = column == null ? void 0 : column.value;
  const label = (column == null ? void 0 : column.label) ?? value ?? "";
  const description = (column == null ? void 0 : column.description) ?? "";
  const changeIcon = columnChangeIcon(columnChangeType);
  const { dragging, getOnDragProps } = useResizeColumn(
    column == null ? void 0 : column.index,
    cellRef,
    dragHandleRef,
    tableId
  );
  const { pinnedColumns, addOrRemovePinnedColumns, disableColumnMenus } = useColumns();
  const findAndReplaceModal = useFindAndReplaceContext();
  const searchValueModal = useSearchValueContext();
  const ref = useRef(null);
  const sortOverrideOrder = convertSortOrder(sortOverride == null ? void 0 : sortOverride.sortOrder);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [{ sortOrder, sortColumn }, setSort] = useState({
    sortOrder: sortOverrideOrder ?? ESortOptions.noOrder,
    sortColumn: sortOverride == null ? void 0 : sortOverride.sortColumn
  });
  const columnSortOrder = (sortOverride == null ? void 0 : sortOverride.sortColumn) === (column == null ? void 0 : column.index) || sortColumn === (column == null ? void 0 : column.index) ? sortOverrideOrder ?? sortOrder : ESortOptions.noOrder;
  const hasMaxPinnedColumns = pinnedColumns.length === MAX_PINNED_COLUMNS;
  const hasSort = columnSortOrder !== ESortOptions.noOrder;
  const isColumnDisabled = readOnly || !!(column == null ? void 0 : column.disabled);
  const isPinDisabled = !pinningEnabled || !pinned && hasMaxPinnedColumns;
  const isAiGenerated = (_a = column == null ? void 0 : column.treatments) == null ? void 0 : _a.includes("ai-defined");
  const handleSort = useCallback(
    (sortSelected) => {
      if (!onSort || (column == null ? void 0 : column.index) === void 0)
        return;
      if (!sortSelected) {
        const { newSortOrder, newSelectedColumn } = determineSort({
          selectedColumn: column == null ? void 0 : column.index,
          currentSortOrder: sortOverrideOrder ?? sortOrder,
          previousColumn: (sortOverride == null ? void 0 : sortOverride.sortColumn) ?? sortColumn ?? void 0
        });
        setSort({ sortOrder: newSortOrder, sortColumn: newSelectedColumn });
        onSort({ columnIndex: newSelectedColumn, order: newSortOrder });
      } else {
        setSort({ sortOrder: sortSelected, sortColumn: column == null ? void 0 : column.index });
        onSort({
          columnIndex: column == null ? void 0 : column.index,
          order: sortSelected,
          contextMenu: true
        });
      }
    },
    [sortOrder, sortColumn, setSort, onSort, sortOverride, absoluteColumnIndex]
  );
  const handlePin = useCallback(() => {
    addOrRemovePinnedColumns(absoluteColumnIndex);
  }, [addOrRemovePinnedColumns, absoluteColumnIndex]);
  const handlePinClick = useCallback(() => {
    onClick();
    handlePin();
  }, [handlePin, onClick]);
  const handleSortClick = useCallback(() => {
    onClick();
    handleSort();
  }, [handleSort, onClick]);
  const handleContextMenuState = useCallback(
    ({ isOpen }) => {
      if (isOpen) {
        onClick();
        setIsMenuOpen(true);
      } else {
        setIsMenuOpen(false);
      }
    },
    [onClick, setIsMenuOpen]
  );
  const handleContextMenu = useCallback(
    (e) => {
      if (e.target.closest("[data-native-context]"))
        return;
      e.preventDefault();
      e.stopPropagation();
      if (!showColumnMenu)
        return;
      handleContextMenuState({ isOpen: true });
    },
    [showColumnMenu, handleContextMenuState]
  );
  useEffect(() => {
    if (ref.current) {
      setIsTruncated(ref.current.scrollWidth > ref.current.offsetWidth);
    }
  }, []);
  useEffect(() => {
    if (sortOverride) {
      setSort({
        sortOrder: convertSortOrder(sortOverride.sortOrder),
        sortColumn: (sortOverride == null ? void 0 : sortOverride.sortColumn) || sortColumn
      });
    }
  }, [sortOverride]);
  const pinnedTooltip = useMemo(() => {
    if (pinned) {
      return t("sheet.table.contextMenu.pin.tooltip.unpin");
    } else if (hasMaxPinnedColumns) {
      return t("sheet.table.contextMenu.pin.tooltip.maxPinned");
    }
    return t("sheet.table.contextMenu.pin.tooltip.pin");
  }, [pinned, hasMaxPinnedColumns]);
  const sortTooltip = useMemo(() => {
    switch (columnSortOrder) {
      case ESortOptions.ascending:
        return t("sheet.table.contextMenu.sort.tooltip.sortedAsc");
      case ESortOptions.descending:
        return t("sheet.table.contextMenu.sort.tooltip.sortedDes");
      default:
        return t("sheet.table.contextMenu.sort.tooltip.notSorted");
    }
  }, [columnSortOrder, sortOverride]);
  const translatedKey = t("sheet.table.header.tooltip.key", {
    key: value
  });
  const translatedLabel = t("sheet.table.header.tooltip.label", {
    label
  });
  const generatedColumnFragment = isAiGenerated ? t("sheet.table.header.tooltip.autoGenerated") + "<br>" : "";
  const headingTooltip = useMemo(() => {
    return isTruncated ? `<div class='tooltip-content'><mono>${generatedColumnFragment}${translatedKey}
${translatedLabel}</mono><span>${description}</span></div>` : `<div class='tooltip-content'><mono>${generatedColumnFragment}${translatedKey}</mono><span>${description}</span></div>`;
  }, [isTruncated, value, description]);
  const hasLinks = useMemo(
    () => htmlStringContainsLinks(description),
    [description]
  );
  const headingTooltipId = hasLinks ? columnHeaderClickableTooltipId(tableId) : columnHeaderTooltipId(tableId);
  const tooltipId = columnHeaderTooltipId(tableId);
  (column == null ? void 0 : column.type) && [EColumnType.enumList, EColumnType.stringList].includes(column == null ? void 0 : column.type);
  const openFindAndReplaceModal = useCallback(
    ({ emptyCells }) => {
      findAndReplaceModal.open({
        columnIndex: absoluteColumnIndex,
        replaceNulls: emptyCells
      });
    },
    [findAndReplaceModal, absoluteColumnIndex]
  );
  const openSearchValueModal = useCallback(() => {
    searchValueModal.open({
      columnIndex: absoluteColumnIndex
    });
  }, [searchValueModal, absoluteColumnIndex]);
  const headerInjectionsComponent = useMemo(() => {
    if (headerInjections) {
      const HeaderInjections = headerInjections;
      return /* @__PURE__ */ jsx("div", { className: "flex items-center", children: /* @__PURE__ */ jsx(HeaderInjections, { field: (column == null ? void 0 : column.value) ?? "" }) });
    }
    return null;
  }, [column, headerInjections]);
  return /* @__PURE__ */ jsxs(
    IndexRowCellInner,
    {
      className: "group",
      pinned: pinningEnabled ? pinned : false,
      menuOpen: isMenuOpen,
      dragging,
      onContextMenu: handleContextMenu,
      children: [
        (column == null ? void 0 : column.disabled) && /* @__PURE__ */ jsx(ColumnHeaderIcon, { children: /* @__PURE__ */ jsx(ReadOnly, {}) }),
        /* @__PURE__ */ jsxs(
          StyledLabel,
          {
            ref,
            "data-tooltip-html": headingTooltip,
            "data-tooltip-id": headingTooltipId,
            children: [
              changeIcon ? /* @__PURE__ */ jsx(Icon, { name: changeIcon, size: 16, className: "mr-2" }) : void 0,
              isAiGenerated ? /* @__PURE__ */ jsx(Icon, { name: "listSparkle", size: 16, className: "mr-2" }) : void 0,
              label
            ]
          }
        ),
        /* @__PURE__ */ jsxs(IndexRowCellRight, { children: [
          (column == null ? void 0 : column.required) && "*",
          /* @__PURE__ */ jsx(
            IndexButtonsContainer,
            {
              hide: isTruncated && !isMenuOpen && !pinned && !hasSort,
              children: /* @__PURE__ */ jsxs(Fragment, { children: [
                pinningEnabled && pinned && /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: `pin-button ${pinned ? "button-selected" : "button-unselected"}`,
                    "data-tooltip-content": pinnedTooltip,
                    "data-tooltip-id": tooltipId,
                    children: /* @__PURE__ */ jsx(
                      IconButton,
                      {
                        isDisabled: isPinDisabled,
                        onPress: handlePinClick,
                        size: 16,
                        name: "pin"
                      }
                    )
                  }
                ),
                !showColumnMenu ? void 0 : onSort ? hasSort && /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: `sort-button ${hasSort ? "button-selected" : "button-unselected"}`,
                    "data-tooltip-content": sortTooltip,
                    "data-tooltip-id": tooltipId,
                    children: /* @__PURE__ */ jsx(
                      SortButton,
                      {
                        onSort: handleSortClick,
                        order: convertButtonSortOrder(columnSortOrder),
                        active: true
                      }
                    )
                  }
                ) : /* @__PURE__ */ jsx("span", {}),
                showColumnMenu && column && /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: "flex grow justify-end items-center gap-0.5",
                    "data-testid": "context-menu-trigger",
                    children: [
                      headerInjectionsComponent,
                      columnContextMenuSlot == null ? void 0 : columnContextMenuSlot({
                        column,
                        disabled: disableColumnMenus,
                        absoluteColumnIndex,
                        isPinned: !!pinned,
                        isPinDisabled,
                        isColumnDisabled,
                        isSortDisabled: !onSort,
                        isLastColumn,
                        columnSortOrder,
                        openFindAndReplaceModal,
                        openSearchValueModal,
                        togglePinnedColumn: addOrRemovePinnedColumns,
                        onMenuOpenChange: setIsMenuOpen
                      })
                    ]
                  }
                )
              ] })
            }
          )
        ] }),
        /* @__PURE__ */ jsx(DragHandle, { ref: dragHandleRef, ...getOnDragProps() })
      ]
    }
  );
};
export {
  ColumnHeaderContent as default
};
